import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight, Link as LinkIcon, Image as ImageIcon,
  Heading2, Heading3, Quote, Undo, Redo
} from "lucide-react";

const MenuButton = ({ onClick, active, children, title }) => (
  <button type="button" onClick={onClick} title={title}
    className={`p-1.5 rounded ${active ? 'bg-[#004E98] text-white' : 'text-slate-600 hover:bg-slate-100'}`}>{children}</button>
);

export function RichEditor({ value, onChange, minHeight = '120px' }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false }),
      Image,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt('URL odkazu:');
    if (url) editor.chain().focus().setLink({ href: url }).run();
  };

  const addImage = () => {
    const url = window.prompt('URL obrazku:');
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  return (
    <div className="border border-slate-300 rounded-lg overflow-hidden focus-within:border-[#004E98] focus-within:ring-1 focus-within:ring-[#004E98]">
      <div className="flex flex-wrap gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50">
        <MenuButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Tucne"><Bold className="w-4 h-4" /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Kurziva"><Italic className="w-4 h-4" /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Podtrzeni"><UnderlineIcon className="w-4 h-4" /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Preskrtnuti"><Strikethrough className="w-4 h-4" /></MenuButton>
        <span className="w-px h-6 bg-slate-300 mx-1 self-center" />
        <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Nadpis 2"><Heading2 className="w-4 h-4" /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Nadpis 3"><Heading3 className="w-4 h-4" /></MenuButton>
        <span className="w-px h-6 bg-slate-300 mx-1 self-center" />
        <MenuButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Odrazky"><List className="w-4 h-4" /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Cislovany"><ListOrdered className="w-4 h-4" /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Citace"><Quote className="w-4 h-4" /></MenuButton>
        <span className="w-px h-6 bg-slate-300 mx-1 self-center" />
        <MenuButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Vlevo"><AlignLeft className="w-4 h-4" /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Na stred"><AlignCenter className="w-4 h-4" /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Vpravo"><AlignRight className="w-4 h-4" /></MenuButton>
        <span className="w-px h-6 bg-slate-300 mx-1 self-center" />
        <MenuButton onClick={addLink} active={editor.isActive('link')} title="Odkaz"><LinkIcon className="w-4 h-4" /></MenuButton>
        <MenuButton onClick={addImage} title="Obrazek"><ImageIcon className="w-4 h-4" /></MenuButton>
        <span className="w-px h-6 bg-slate-300 mx-1 self-center" />
        <MenuButton onClick={() => editor.chain().focus().undo().run()} title="Zpet"><Undo className="w-4 h-4" /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().redo().run()} title="Vpred"><Redo className="w-4 h-4" /></MenuButton>
      </div>
      <EditorContent editor={editor} className="prose max-w-none" style={{ minHeight }} />
      <style>{`.ProseMirror{padding:12px;outline:none;min-height:${minHeight}} .ProseMirror p{margin:4px 0} .ProseMirror h2{font-size:1.25rem;font-weight:600;margin:8px 0} .ProseMirror h3{font-size:1.1rem;font-weight:600;margin:6px 0} .ProseMirror ul,.ProseMirror ol{padding-left:24px} .ProseMirror blockquote{border-left:3px solid #ddd;padding-left:12px;color:#666} .ProseMirror img{max-width:100%;height:auto;border-radius:4px}`}</style>
    </div>
  );
}
